import React, { memo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { validatePhotoFile } from 'molecules/PhotoUpload';
import { careerColor } from 'services/careerTokens';
import { careerLayout, careerType } from 'services/careerType';

interface ICvPhotoField {
  /** Что показывать: выбранный файл (blob) или фото с сервера. */
  imageUrl: string;
  onChange: (file: File | '') => void;
  disabled?: boolean;
}

/**
 * Фото в данных CV (0072, шаг 2).
 *
 * Аудит: перед редактором стояла большая отдельная секция фотографии. Теперь
 * это маленький аватар в «Informacje podstawowe» и две кнопки. Файл уходит на
 * сервер вместе со всем остальным по «Zapisz zmiany» — выбор фото сам ничего
 * не сохраняет, и экран честно говорит «Niezapisane zmiany».
 */
export const CvPhotoField = memo(({ imageUrl, onChange, disabled = false }: ICvPhotoField) => {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState(false);

  return (
    <Row>
      <Avatar aria-hidden={!imageUrl}>
        {imageUrl ? <img src={imageUrl} alt={t('careerProfile.photo.alt')} /> : null}
      </Avatar>
      <Body>
        <Actions>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg"
            hidden
            onChange={async (event) => {
              const file = event.target.files?.[0];
              // Тот же файл второй раз подряд должен снова вызвать onChange.
              event.target.value = '';
              if (!file) return;
              const ok = await validatePhotoFile(file);
              setError(!ok);
              if (ok) onChange(file);
            }}
          />
          <Action type="button" disabled={disabled} onClick={() => input.current?.click()}>
            {imageUrl ? t('careerProfile.photo.change') : t('careerProfile.photo.add')}
          </Action>
          {imageUrl ? (
            <Action type="button" disabled={disabled} onClick={() => onChange('')}>
              {t('careerProfile.photo.clear')}
            </Action>
          ) : null}
        </Actions>
        <Hint role={error ? 'alert' : undefined} $error={error}>
          {error ? t('input.validation.photoWrongFormat') : t('careerProfile.photo.info')}
        </Hint>
      </Body>
    </Row>
  );
});

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
`;

const Avatar = styled.span`
  flex: none;
  width: 64px;
  height: 64px;
  overflow: hidden;
  border-radius: 50%;
  background: ${careerColor.hover};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Action = styled.button`
  min-height: ${careerLayout.hitTarget}px;
  padding: 0 16px;
  border: 1px solid ${careerColor.line};
  border-radius: ${careerLayout.controlRadius}px;
  background: transparent;
  color: ${careerColor.text};
  cursor: pointer;
  ${careerType.body()};

  &:hover:enabled {
    background: ${careerColor.hover};
  }
`;

const Hint = styled.p<{ $error: boolean }>`
  margin: 0;
  color: ${({ $error }) => ($error ? '#c1121f' : careerColor.text2)};
  ${careerType.meta()};
`;
